import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Sparkles, Zap, Crown } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

// TODO: replace with real Stripe price IDs from your Stripe dashboard
const STRIPE_PRICE_IDS: Record<string, string> = {
  pro: 'price_xxx_pro',
  quant: 'price_xxx_quant',
};

const tiers = [
  {
    id: 'free' as const,
    name: 'Free',
    price: '$0',
    period: 'forever',
    icon: Zap,
    features: [
      'Demo trading only',
      'Up to 2 strategy instances',
      'AI signals (view only)',
      'Basic market data',
      'Community support',
    ],
    cta: 'Current Plan',
    highlight: false,
  },
  {
    id: 'pro' as const,
    name: 'Pro',
    price: '$29',
    period: '/month',
    icon: Sparkles,
    features: [
      'Live trading enabled',
      'Up to 3 broker connections',
      'Up to 10 strategy instances',
      'AI signals: approve & execute',
      'Advanced market analytics',
      'Email support',
    ],
    cta: 'Upgrade to Pro',
    highlight: true,
  },
  {
    id: 'quant' as const,
    name: 'Quant',
    price: '$79',
    period: '/month',
    icon: Crown,
    features: [
      'Everything in Pro',
      'Unlimited brokers',
      'Unlimited strategies',
      'AI auto-execute signals',
      'API access (coming soon)',
      'Priority support',
    ],
    cta: 'Upgrade to Quant',
    highlight: false,
  },
];

const PricingPage = () => {
  const { tier: currentTier } = useSubscription();

  const handleUpgrade = async (tierId: string) => {
    if (tierId === currentTier) return;
    if (tierId === 'free') return;
    const priceId = STRIPE_PRICE_IDS[tierId];
    if (!priceId) {
      toast({ title: 'Plan unavailable', variant: 'destructive' });
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({ title: 'Sign in required', description: 'Please sign in to upgrade.' });
      return;
    }
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { priceId, userId: user.id },
      });
      if (error || !data?.url) {
        toast({ title: 'Checkout error', description: error?.message ?? 'Could not start checkout', variant: 'destructive' });
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      console.error('checkout invoke failed', err);
      toast({ title: 'Checkout error', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold tracking-tight">Choose Your Plan</h2>
        <p className="text-muted-foreground text-sm">
          Scale your automated trading with the right tier
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tiers.map((t) => {
          const Icon = t.icon;
          const isCurrent = currentTier === t.id;
          return (
            <Card
              key={t.id}
              className={`relative border-border/30 bg-card/60 transition-all ${
                t.highlight
                  ? 'ring-2 ring-primary/40 shadow-[0_0_24px_hsl(var(--primary)/0.1)]'
                  : ''
              }`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground text-[10px] px-3">
                    Most Popular
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <div className="w-10 h-10 mx-auto rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-lg">{t.name}</CardTitle>
                <div className="flex items-baseline justify-center gap-1 mt-1">
                  <span className="text-3xl font-bold">{t.price}</span>
                  <span className="text-sm text-muted-foreground">{t.period}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2.5">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={isCurrent ? 'outline' : t.highlight ? 'default' : 'secondary'}
                  disabled={isCurrent}
                  onClick={() => handleUpgrade(t.id)}
                >
                  {isCurrent ? 'Current Plan' : t.cta}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PricingPage;
